package com.shadowings.gero.presentation

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTransformGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.geometry.isSpecified
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.shadowings.gero.data.repository.ImageCredits
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.presentation.ui.AppTheme
import compose.icons.FeatherIcons
import compose.icons.feathericons.XCircle
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.combo_title
import gero.composeapp.generated.resources.image_popup_credit
import gero.composeapp.generated.resources.tutankhamon2
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Preview
@Composable
fun ImagePopupPreview() {
    val item = ExplanationContent(
        title = Res.string.combo_title,
        face = Faces.Happy
    )
    AppTheme {
        Surface {
            ImagePopupContent(
                resource = Res.drawable.tutankhamon2,
                contentDescription = stringResource(item.title),
                applyFilter = item.applyFilter,
                onClose = {}
            )
        }
    }
}

@Composable
fun ImagePopup(
    showFullscreenImage: MutableState<Boolean>,
    resource: DrawableResource,
    item: ExplanationContent
) {
    ImagePopup(
        showFullscreenImage = showFullscreenImage,
        resource = resource,
        contentDescription = item.content?.let { stringResource(it, *item.contentArgs.toTypedArray()) }
            ?: stringResource(item.title),
        applyFilter = item.applyFilter
    )
}

@Composable
fun ImagePopup(
    showFullscreenImage: MutableState<Boolean>,
    resource: DrawableResource,
    contentDescription: String?,
    applyFilter: Boolean,
    title: String? = null,
    description: String? = null
) {
    Dialog(
        onDismissRequest = { showFullscreenImage.value = false },
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        ImagePopupContent(
            resource = resource,
            contentDescription = contentDescription,
            applyFilter = applyFilter,
            title = title,
            description = description,
            onClose = { showFullscreenImage.value = false }
        )
    }
}

internal data class ImageTransformResult(
    val zoom: Float,
    val panOffset: Offset
)

internal fun calculateImageTransform(
    currentZoom: Float,
    currentPan: Offset,
    zoomChange: Float,
    panDelta: Offset,
    centroid: Offset,
    viewportSize: Size,
    intrinsicImageSize: Size? = null,
    minZoom: Float = 1f,
    maxZoom: Float = 6f
): ImageTransformResult {
    val viewWidth = viewportSize.width
    val viewHeight = viewportSize.height
    if (viewWidth <= 0f || viewHeight <= 0f) {
        return ImageTransformResult(currentZoom, currentPan)
    }

    val targetZoom = (currentZoom * zoomChange).coerceIn(minZoom, maxZoom)
    if (targetZoom <= 1f) {
        return ImageTransformResult(1f, Offset.Zero)
    }

    val effectiveZoomRatio = if (currentZoom > 0f) targetZoom / currentZoom else 1f
    val center = Offset(viewWidth / 2f, viewHeight / 2f)

    val (fittedWidth, fittedHeight) = if (
        intrinsicImageSize != null &&
        intrinsicImageSize.isSpecified &&
        intrinsicImageSize.width > 0f &&
        intrinsicImageSize.height > 0f &&
        !intrinsicImageSize.width.isInfinite() &&
        !intrinsicImageSize.width.isNaN() &&
        !intrinsicImageSize.height.isInfinite() &&
        !intrinsicImageSize.height.isNaN()
    ) {
        val scaleFactor = minOf(
            viewWidth / intrinsicImageSize.width,
            viewHeight / intrinsicImageSize.height
        )
        Pair(intrinsicImageSize.width * scaleFactor, intrinsicImageSize.height * scaleFactor)
    } else {
        Pair(viewWidth, viewHeight)
    }

    val scaledWidth = fittedWidth * targetZoom
    val scaledHeight = fittedHeight * targetZoom

    val maxPanX = maxOf(0f, (scaledWidth - viewWidth) / 2f)
    val maxPanY = maxOf(0f, (scaledHeight - viewHeight) / 2f)

    val effectivePanDelta = if (currentZoom > 1f || targetZoom > 1f) panDelta else Offset.Zero
    val proposedPan = (currentPan * effectiveZoomRatio) +
        (centroid - center) * (1f - effectiveZoomRatio) +
        effectivePanDelta

    val finalPan = Offset(
        x = proposedPan.x.coerceIn(-maxPanX, maxPanX),
        y = proposedPan.y.coerceIn(-maxPanY, maxPanY)
    )

    return ImageTransformResult(targetZoom, finalPan)
}

@Composable
private fun ImagePopupContent(
    resource: DrawableResource,
    contentDescription: String?,
    applyFilter: Boolean,
    title: String? = null,
    description: String? = null,
    onClose: () -> Unit
) {
    var zoom by remember { mutableFloatStateOf(1f) }
    var panOffset by remember { mutableStateOf(Offset.Zero) }

    LaunchedEffect(resource) {
        zoom = 1f
        panOffset = Offset.Zero
    }

    val painter = painterResource(resource)
    val intrinsicSize = painter.intrinsicSize

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.scrim),
        contentAlignment = Alignment.Center
    ) {
        Image(
            modifier = Modifier
                .fillMaxSize()
                .pointerInput(resource) {
                    detectTransformGestures(panZoomLock = true) { centroid, pan, zoomChange, _ ->
                        val result = calculateImageTransform(
                            currentZoom = zoom,
                            currentPan = panOffset,
                            zoomChange = zoomChange,
                            panDelta = pan,
                            centroid = centroid,
                            viewportSize = Size(size.width.toFloat(), size.height.toFloat()),
                            intrinsicImageSize = intrinsicSize
                        )
                        zoom = result.zoom
                        panOffset = result.panOffset
                    }
                }
                .graphicsLayer {
                    scaleX = zoom
                    scaleY = zoom
                    translationX = panOffset.x
                    translationY = panOffset.y
                },
            painter = painter,
            contentDescription = contentDescription,
            contentScale = ContentScale.Fit,
            colorFilter = if (applyFilter) {
                ColorFilter.tint(MaterialTheme.colorScheme.onSurface)
            } else {
                null
            }
        )

        IconButton(
            modifier = Modifier
                .align(Alignment.TopStart)
                .clickable(onClick = onClose)
                .semantics { testTag = "image_popup_clickable_close" }
                .size(48.dp),
            onClick = onClose
        ) {
            Icon(
                imageVector = FeatherIcons.XCircle,
                contentDescription = null,
                tint = Color.White,
                modifier = Modifier.size(24.dp)
            )
        }

        ImageInfoCard(
            title = title,
            description = description,
            credit = ImageCredits.forResource(resource),
            modifier = Modifier.align(Alignment.BottomCenter)
        )
    }
}

// Small card at the bottom of the fullscreen popup with the image title, description and attribution.
@Composable
private fun ImageInfoCard(
    title: String?,
    description: String?,
    credit: String?,
    modifier: Modifier = Modifier
) {
    val lines = listOfNotNull(title?.takeIf { it.isNotBlank() }, description?.takeIf { it.isNotBlank() }, credit)
    if (lines.isEmpty()) return
    Surface(
        modifier = modifier
            .fillMaxWidth()
            .navigationBarsPadding()
            .padding(12.dp)
            .semantics { testTag = "image_popup_info_card" },
        shape = RoundedCornerShape(12.dp),
        color = MaterialTheme.colorScheme.surface.copy(alpha = 0.9f)
    ) {
        Column(modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp)) {
            title?.takeIf { it.isNotBlank() }?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.titleSmall.copy(fontWeight = FontWeight.Bold),
                    color = MaterialTheme.colorScheme.onSurface
                )
            }
            description?.takeIf { it.isNotBlank() }?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurface
                )
            }
            credit?.let {
                Text(
                    text = stringResource(Res.string.image_popup_credit, it),
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}